import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="variant-selector"
export default class extends Controller {
  static targets = ["optionInput", "skuInput", "priceDisplay", "compareDisplay", "selectedLabel", "buyNowLink", "stockStatus"]
  static values = {
    variants: Array,
    // A Product Display can be embedded anywhere (home page, landing page,
    // group). Selecting its default SKU must not mutate that page's URL.
    // The product-detail template opts into this explicitly.
    syncUrl: { type: Boolean, default: false }
  }

  connect() {
    // Pre-select from URL params
    const urlParams = new URLSearchParams(window.location.search)
    const skuParam = urlParams.get("sku_id")

    if (skuParam && this.variantsValue.length > 0) {
      const matched = this.variantsValue.find(v => String(v.id) === String(skuParam))
      if (matched && matched.options) {
        matched.options.forEach((optValue, idx) => {
          const groupInputs = this.optionInputTargets.filter(inp => inp.name === `option_${idx}`)
          groupInputs.forEach(inp => {
            inp.checked = (inp.value === optValue)
          })
        })
      }
    }

    this._updateVariant()
  }

  select() {
    this._updateVariant()
  }

  _updateVariant() {
    if (this.variantsValue.length === 0) return

    const selectedOptions = this._getSelectedOptions()
    const matched = this._findMatchingVariant(selectedOptions)

    if (matched) {
      // Update hidden sku_id input
      if (this.hasSkuInputTarget) {
        this.skuInputTarget.value = matched.id
      }

      // Update price display
      if (this.hasPriceDisplayTarget) {
        this.priceDisplayTarget.innerText = matched.price_money
      }

      // Per-product discount: strike-through original price for this variant
      // (hidden when the variant isn't discounted).
      this.compareDisplayTargets.forEach((el) => {
        el.innerText = matched.compare_at_money || ""
        el.classList.toggle("hidden", !matched.compare_at_money)
      })

      // Unified Commerce Core — Buy Now link always points at the
      // currently selected variant (never at the Cart, never at
      // whichever variant was default on page load).
      if (this.hasBuyNowLinkTarget) {
        this.buyNowLinkTarget.href = `/checkout/buy-now?sku_id=${matched.id}`
      }

      // Keep the selected SKU in the URL only on a product-detail page.
      // A reusable Product Display may have a single default SKU; updating
      // history there turns e.g. the homepage into `/?sku_id=2` on load.
      if (this.syncUrlValue) {
        try {
          const url = new URL(window.location.href)
          url.searchParams.set("sku_id", matched.id)
          window.history.replaceState({}, "", url.toString())
        } catch (e) { /* ignore */ }
      }

      this._applyStock(matched)

      // Update selected labels
      if (this.hasSelectedLabelTarget) {
        this.selectedLabelTargets.forEach((label, idx) => {
          label.innerText = selectedOptions[idx] || ""
        })
      }

      // Dispatch event for other controllers (e.g. cart controller)
      this.dispatch("changed", { detail: { variant: matched } })
    }
  }

  // Reflect the selected variant's stock: sold-out disables add-to-cart and
  // Buy Now, a tracked variant caps the quantity inputs. Presentation only —
  // the server re-checks atomically at cart/checkout.
  _applyStock(variant) {
    const soldOut = variant.available === false
    const cap = variant.max_quantity

    this.element.querySelectorAll('[data-cart-target="submitButton"]').forEach((btn) => {
      btn.disabled = soldOut
      btn.classList.toggle("opacity-50", soldOut)
      btn.classList.toggle("cursor-not-allowed", soldOut)
    })

    if (this.hasBuyNowLinkTarget) {
      const link = this.buyNowLinkTarget
      link.setAttribute("aria-disabled", String(soldOut))
      link.classList.toggle("pointer-events-none", soldOut)
      link.classList.toggle("opacity-50", soldOut)
    }

    this.element.querySelectorAll('input[name="quantity"]').forEach((input) => {
      if (cap === null || cap === undefined) input.removeAttribute("max")
      else input.max = Math.max(cap, 1)
      if (input.max && Number(input.value) > Number(input.max)) input.value = input.max
    })

    this.stockStatusTargets.forEach((el) => {
      el.innerText = soldOut ? el.dataset.soldOut : el.dataset.inStock
      el.classList.toggle("text-red-500", soldOut)
      el.classList.toggle("text-green-600", !soldOut)
    })
  }

  _getSelectedOptions() {
    const options = []
    const groupNames = [...new Set(this.optionInputTargets.map(inp => inp.name))]

    groupNames.forEach(name => {
      const checked = this.optionInputTargets.find(inp => inp.name === name && inp.checked)
      if (checked) options.push(checked.value)
    })

    return options
  }

  _findMatchingVariant(selectedOptions) {
    return this.variantsValue.find(variant => {
      if (!variant.options) return false
      for (let i = 0; i < selectedOptions.length; i++) {
        if (String(variant.options[i] || "").trim() !== String(selectedOptions[i] || "").trim()) {
          return false
        }
      }
      return true
    })
  }
}
